/**
 * Layout Component
 *
 * Main application layout with shadcn sidebar
 */

import { useLocation, useNavigate } from 'react-router-dom';

import { ChevronsUpDown, LogOut, User, Settings } from 'lucide-react';
import { toast } from 'sonner';

import { AppSidebar } from './AppSidebar';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { ViewerProvider } from '@/contexts/ViewerContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuthQuery } from '@/features/auth/api/auth.queries';
import { clearAuthData } from '@/services/api/tokenStorage';

interface LayoutProps {
  className?: string;
  children?: React.ReactNode;
}

// Page title mapping
const getPageTitle = (pathname: string): string => {
  const titles: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/new-flight': 'New Flight',
    '/live-operation': 'Live Operations',
    '/asset-management/gear': 'Asset Management - Gear',
    '/asset-management/pilot': 'Asset Management - Pilot',
    '/asset-management/items': 'Asset Management - Items',
    '/mission': 'Missions',
    '/mission-planning/area-of-interest': 'Area of Interest',
    '/mission-history': 'Mission History',
    '/settings': 'Settings',
  };
  return titles[pathname] || 'Dashboard';
};

// Helper to get avatar initials
const getAvatarInitials = (name?: string): string => {
  if (!name) return 'U';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getPageTitle(location.pathname);
  const { userProfile } = useAuthQuery();

  // User data from auth
  const userData = {
    name: userProfile?.nrp || 'User',
    email: userProfile?.role || 'user@dronemp.id',
    avatar: getAvatarInitials(userProfile?.nrp),
  };

  const handleLogout = () => {
    clearAuthData();
    toast.success('Successfully logged out');
    navigate('/login');
  };

  return (
    <ViewerProvider viewer={undefined}>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-700 bg-slate-900 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-lg font-semibold text-sidebar-foreground">
              {pageTitle}
            </h1>
            <div className="flex-1" />

            {/* User Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-blue-600 text-white text-sm">
                      {userData.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:grid text-left text-sm leading-tight">
                    <span className="truncate font-semibold text-sidebar-foreground">
                      {userData.name}
                    </span>
                    <span className="truncate text-xs text-sidebar-foreground/70">
                      {userData.email}
                    </span>
                  </div>
                  <ChevronsUpDown className="size-4 text-sidebar-foreground/70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-56 rounded-lg bg-slate-900"
                side="bottom"
                align="end"
                sideOffset={12}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-blue-600 text-white">
                        {userData.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {userData.name}
                      </span>
                      <span className="truncate text-xs">{userData.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <User className="mr-2 size-4" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings className="mr-2 size-4" />
                  Account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 size-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          <main className="flex-1 overflow-hidden bg-slate-950">
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </ViewerProvider>
  );
}
